import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Link2, CheckCircle2, RefreshCcw, AlertTriangle, X, Instagram as InstagramIcon, FileSpreadsheet, ExternalLink } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import useRequest from "../../../components/hook/use-req";
import { useGoogleSheetsStatus, useConnectGoogleSheets, useDisconnectGoogleSheets, useRetryGoogleSheets, GoogleSheetsStatus } from "./google-sheets-api";

// Marketing Integrations — Instagram is wired to the real API
// (instagram.postman_collection.json): connect via Facebook OAuth,
// list/rename/toggle/disconnect accounts. WhatsApp has no equivalent doc
// yet, so its card stays a plain "Get Started" placeholder rather than
// guessing an endpoint.

const CONNECT_BASE = process.env.REACT_APP_BACKEND_URL || "https://api.connect.dalmlabs.com";

interface InstagramAccount {
  ig_user_id: string;
  ig_username?: string;
  page_name?: string;
  name?: string;
  is_active?: boolean;
  created_at?: string;
  connected_at?: string;
}

type Banner = { kind: "success" | "info" | "error"; title: string; description: string };

const formatDate = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d
    .toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })
    .replace(",", "")
    .replace(":", ".");
};

const secondaryLabel = (a: InstagramAccount) => a.page_name || a.name || a.ig_user_id;

const StatusPill: React.FC<{ connected: boolean }> = ({ connected }) => (
  <span
    className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
      connected ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400" : "bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400"
    }`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-emerald-500" : "bg-rose-500"}`} />
    {connected ? "Connected" : "Not Connected"}
  </span>
);

const Toggle: React.FC<{ checked: boolean; onChange: () => void; disabled?: boolean }> = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={onChange}
    disabled={disabled}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 disabled:opacity-50 ${
      checked ? "bg-emerald-500" : "bg-gray-200 dark:bg-gray-700"
    }`}
  >
    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-[18px]" : "translate-x-1"}`} />
  </button>
);

const BannerCard: React.FC<{ banner: Banner; onDismiss: () => void }> = ({ banner, onDismiss }) => {
  const styles: Record<Banner["kind"], { bg: string; icon: React.ReactNode }> = {
    success: {
      bg: "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-100 dark:border-emerald-500/20",
      icon: (
        <span className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
          <CheckCircle2 size={16} className="text-white" />
        </span>
      ),
    },
    info: {
      bg: "bg-indigo-50 dark:bg-indigo-500/10 border-indigo-100 dark:border-indigo-500/20",
      icon: (
        <span className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center shrink-0">
          <RefreshCcw size={16} className="text-indigo-600 dark:text-indigo-400" />
        </span>
      ),
    },
    error: {
      bg: "bg-rose-50 dark:bg-rose-500/10 border-rose-100 dark:border-rose-500/20",
      icon: <AlertTriangle size={20} className="text-rose-500 shrink-0" />,
    },
  };
  const s = styles[banner.kind];
  return (
    <div className={`flex items-start justify-between gap-3 rounded-xl border p-4 mb-5 ${s.bg}`}>
      <div className="flex items-start gap-3">
        {s.icon}
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{banner.title}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{banner.description}</p>
        </div>
      </div>
      <button type="button" onClick={onDismiss} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0">
        <X size={16} />
      </button>
    </div>
  );
};

// ── Disconnect confirmation ─────────────────────────────────────────────────

const DisconnectConfirmModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  submitting: boolean;
  title?: string;
  description?: string;
  confirmLabel?: string;
}> = ({
  isOpen,
  onClose,
  onConfirm,
  submitting,
  title = "Disconnect Instagram Account?",
  description = "Your scheduled broadcasts and reporting will stop sending or syncing after disconnection.",
  confirmLabel = "Disconnect Account",
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 text-center">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center mb-4">
          <AlertTriangle size={26} className="text-indigo-500" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{description}</p>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="px-4 py-2.5 text-sm font-semibold text-white bg-rose-500 hover:bg-rose-600 rounded-lg disabled:opacity-60"
          >
            {submitting ? "Disconnecting…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Manage connected accounts ───────────────────────────────────────────────

const ManageAccountsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  accounts: InstagramAccount[];
  onToggle: (account: InstagramAccount) => void;
  onRequestDisconnect: (account: InstagramAccount) => void;
  togglingId: string | null;
}> = ({ isOpen, onClose, accounts, onToggle, onRequestDisconnect, togglingId }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between px-7 pt-6 pb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Manage Connected Accounts</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage all your Instagram connected accounts</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={20} />
          </button>
        </div>

        <div className="px-7 max-h-[420px] overflow-y-auto space-y-3 pb-2">
          {accounts.map((a) => (
            <div key={a.ig_user_id} className="flex items-center justify-between gap-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl px-4 py-3.5">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                  <Link2 size={15} className="text-white" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">@{a.ig_username || a.ig_user_id}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{secondaryLabel(a)}</p>
                </div>
              </div>
              <div className="flex items-center gap-6 shrink-0">
                <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                  Connected Since
                  <br />
                  <span className="text-gray-700 dark:text-gray-200 font-medium">{formatDate(a.created_at || a.connected_at)}</span>
                </div>
                <button type="button" onClick={() => onRequestDisconnect(a)} className="text-sm font-medium text-rose-500 hover:underline whitespace-nowrap">
                  Disconnect
                </button>
                <div className="flex flex-col items-center gap-1">
                  <Toggle checked={!!a.is_active} onChange={() => onToggle(a)} disabled={togglingId === a.ig_user_id} />
                  <span className="text-[11px] text-gray-500 dark:text-gray-400">{a.is_active ? "Active" : "Inactive"}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2.5 px-7 py-5 mt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm">
            Save and Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const Integrations: React.FC = () => {
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const location = useLocation();

  const [accounts, setAccounts] = useState<InstagramAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState<InstagramAccount | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}`, "X-AUTH-TYPE": "jwt" };
  const { makeRequest: getAccounts } = useRequest("/instagram/accounts", "GET", authHeaders);
  const { makeRequest: patchAccount } = useRequest("/instagram/accounts", "PATCH", authHeaders);
  const { makeRequest: deleteAccount } = useRequest("/instagram/accounts", "DELETE", authHeaders);

  const fetchAccounts = async () => {
    setLoading(true);
    const [response, status] = await getAccounts();
    if (status >= 200 && status < 300) {
      const list: InstagramAccount[] = Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : [];
      setAccounts(list);
    } else {
      setAccounts([]);
    }
    setLoading(false);
  };

  // Facebook's OAuth callback 302s back to `{fe_portal_uri}/settings/integrations
  // ?instagram=connected` (or `?instagram=failed&error=...`) — this page is
  // also mounted at that alternate path (see main-routes.tsx) so either
  // landing spot resolves the banner correctly.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const result = params.get("instagram");
    if (result === "connected") {
      setBanner({
        kind: "success",
        title: "Instagram account connected successfully",
        description: "You can now  publish Instagram content, sync engagement and track broadcast performance in ConvoHub",
      });
    } else if (result === "failed") {
      setBanner({
        kind: "error",
        title: "Connection Unsuccessful",
        description: "Something went wrong while connecting your account. Please try again.",
      });
    }
    if (result) navigate(location.pathname, { replace: true });
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Google Sheets (bot leads) ──────────────────────────────────────────
  const [sheetsStatus, setSheetsStatus] = useState<GoogleSheetsStatus | null>(null);
  const [sheetsLoading, setSheetsLoading] = useState(true);
  const [sheetsConnecting, setSheetsConnecting] = useState(false);
  const [sheetsDisconnecting, setSheetsDisconnecting] = useState(false);
  const [sheetsRetrying, setSheetsRetrying] = useState(false);
  const [sheetsDisconnectOpen, setSheetsDisconnectOpen] = useState(false);
  const [awaitingSheetsAuth, setAwaitingSheetsAuth] = useState(false);

  const { get: getSheetsStatus } = useGoogleSheetsStatus();
  const { connect: connectSheets } = useConnectGoogleSheets();
  const { disconnect: disconnectSheets } = useDisconnectGoogleSheets();
  const { retry: retrySheets } = useRetryGoogleSheets();

  const fetchSheetsStatus = useCallback(async (): Promise<GoogleSheetsStatus | null> => {
    const s = await getSheetsStatus();
    setSheetsStatus(s);
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      setSheetsLoading(true);
      await fetchSheetsStatus();
      setSheetsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The Google OAuth consent screen opens in a new tab (same pattern as
  // Instagram's below) — this app never sees the `/callback` leg directly
  // (Google hits the backend, not us), so there's no query param we can
  // reliably wait for here. Instead: refetch the moment the operator comes
  // back to this tab, and back that up with a short bounded poll in case the
  // focus event doesn't fire (common on some mobile browsers).
  useEffect(() => {
    if (!awaitingSheetsAuth) return;
    const onFocus = async () => {
      const s = await fetchSheetsStatus();
      if (s?.sheet_url) setAwaitingSheetsAuth(false);
    };
    window.addEventListener("focus", onFocus);
    const pollStart = Date.now();
    const pollId = window.setInterval(async () => {
      if (Date.now() - pollStart > 120000) {
        window.clearInterval(pollId);
        setAwaitingSheetsAuth(false);
        return;
      }
      const s = await fetchSheetsStatus();
      if (s?.sheet_url) {
        window.clearInterval(pollId);
        setAwaitingSheetsAuth(false);
      }
    }, 4000);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.clearInterval(pollId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingSheetsAuth]);

  const handleConnectSheets = async () => {
    setSheetsConnecting(true);
    const res = await connectSheets();
    setSheetsConnecting(false);
    if (res?.authorization_url) {
      window.open(res.authorization_url, "_blank", "noopener,noreferrer");
      setAwaitingSheetsAuth(true);
    } else {
      toast.error("Couldn't start the Google Sheets connection.");
    }
  };

  const handleDisconnectSheets = async () => {
    setSheetsDisconnecting(true);
    const ok = await disconnectSheets();
    setSheetsDisconnecting(false);
    setSheetsDisconnectOpen(false);
    if (ok) {
      setSheetsStatus(null);
      toast.success("Google Sheets disconnected.");
    } else {
      toast.error("Couldn't disconnect Google Sheets.");
    }
  };

  const handleRetrySheets = async () => {
    setSheetsRetrying(true);
    const res = await retrySheets();
    setSheetsRetrying(false);
    if (res) {
      toast.success(`${res.queued ?? 0} failed row${res.queued === 1 ? "" : "s"} queued for retry.`);
      fetchSheetsStatus();
    } else {
      toast.error("Couldn't retry the failed rows.");
    }
  };

  const sheetsConnected = Boolean(sheetsStatus?.sheet_url);

  const startInstagramOAuth = async () => {
    if (!token) {
      toast.error("You need to be signed in to connect Instagram.");
      return;
    }
    // The API doc's claim that this route reads the portal's own session
    // instead of the Authorization header doesn't hold for this app — this
    // SPA only ever carries a JWT in localStorage, no session cookie, so a
    // plain window.open() (which can't attach a custom header) gets back
    // exactly the "authorization: Field required" 422 you saw. Call it via
    // an authenticated fetch instead: if the backend answers with JSON
    // containing the Facebook URL, open that; if it still does a raw 302,
    // the browser can only follow that transparently when the destination
    // allows cross-origin reads, which Facebook's OAuth dialog doesn't, so
    // that path surfaces a clear error instead of failing silently.
    setConnecting(true);
    try {
      const res = await fetch(`${CONNECT_BASE}/instagram/oauth/start`, {
        headers: { Authorization: `Bearer ${token}`, "X-AUTH-TYPE": "jwt", Accept: "application/json" },
      });

      let redirectUrl: string | undefined;
      try {
        const data = await res.clone().json();
        redirectUrl = data?.url || data?.redirect_url || data?.oauth_url || data?.authorization_url;
      } catch {
        // Not a JSON body — fall through to the redirect checks below.
      }

      if (redirectUrl) {
        window.open(redirectUrl, "_blank", "noopener,noreferrer");
      } else if (res.redirected && res.url) {
        window.open(res.url, "_blank", "noopener,noreferrer");
      } else if (!res.ok) {
        const data = await res.json().catch(() => null);
        const msg = Array.isArray(data?.detail) ? data.detail[0]?.msg : data?.detail;
        toast.error(msg || "Failed to start the Instagram connection.");
      } else {
        toast.error("Instagram sign-in didn't return a redirect — this endpoint may need to respond with the Facebook URL as JSON for this app.");
      }
    } catch {
      toast.error("Couldn't reach Instagram sign-in. This route may need to return the Facebook URL as JSON instead of a raw redirect for a browser-based app like this one.");
    } finally {
      setConnecting(false);
    }
  };

  const handleToggleActive = async (account: InstagramAccount) => {
    setTogglingId(account.ig_user_id);
    const [response, status] = await patchAccount({ is_active: !account.is_active }, undefined, account.ig_user_id);
    setTogglingId(null);
    if (status >= 200 && status < 300) {
      setAccounts((prev) => prev.map((a) => (a.ig_user_id === account.ig_user_id ? { ...a, is_active: !account.is_active } : a)));
    } else {
      toast.error(response?.detail || response?.message || "Failed to update the account.");
    }
  };

  const handleDisconnect = async () => {
    if (!disconnectTarget) return;
    setDisconnecting(true);
    const [response, status] = await deleteAccount(undefined, undefined, disconnectTarget.ig_user_id);
    setDisconnecting(false);
    if (status >= 200 && status < 300) {
      setAccounts((prev) => prev.filter((a) => a.ig_user_id !== disconnectTarget.ig_user_id));
      setDisconnectTarget(null);
      setBanner({
        kind: "info",
        title: "Instagram disconnected",
        description: "You can now  publish Instagram content, sync engagement and track broadcast performance in ConvoHub",
      });
    } else {
      toast.error(response?.detail || response?.message || "Failed to disconnect the account.");
    }
  };

  const primaryAccount = accounts[0];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <div className=" mx-auto space-y-1">
        <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-full px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 mb-6 w-fit">
          <Link2 size={15} className="text-gray-400" />
          Connect your channels to power broadcasts and conversations.
        </div>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Marketing Integrations</h1>
        <p className="text-[12px] font-semibold text-gray-500 dark:text-gray-100 mb-6">Connect your outbound channels to publish, engage and measure broadcast performance</p>

        {banner && <BannerCard banner={banner} onDismiss={() => setBanner(null)} />}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Instagram */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-5">
            <div className="flex items-start justify-between gap-3 mb-1.5">
              <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-xl bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600 flex items-center justify-center shrink-0">
                  <InstagramIcon size={18} className="text-white" />
                </span>
                <span className="text-base font-semibold text-gray-900 dark:text-gray-100">Instagram</span>
                <StatusPill connected={!!primaryAccount} />
              </div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg px-2.5 py-1 shrink-0">
                {accounts.length} account{accounts.length === 1 ? "" : "s"}
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Connect your Instagram to run and track Instagram broadcasts through ConvoHub</p>

            {loading ? (
              <div className="h-10 flex items-center text-xs text-gray-400 dark:text-gray-500">Loading…</div>
            ) : primaryAccount ? (
              <>
                <div className="flex items-center justify-between gap-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl px-4 py-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                      <Link2 size={13} className="text-white" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">@{primaryAccount.ig_username || primaryAccount.ig_user_id}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{secondaryLabel(primaryAccount)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-xs text-gray-500 dark:text-gray-400 text-right hidden sm:block">
                      Connected Since
                      <br />
                      <span className="text-gray-700 dark:text-gray-200 font-medium">{formatDate(primaryAccount.created_at || primaryAccount.connected_at)}</span>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <Toggle checked={!!primaryAccount.is_active} onChange={() => handleToggleActive(primaryAccount)} disabled={togglingId === primaryAccount.ig_user_id} />
                      <span className="text-[11px] text-gray-500 dark:text-gray-400">{primaryAccount.is_active ? "Active" : "Inactive"}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setManageOpen(true)}
                    className="px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Manage Connections
                  </button>
                  <button
                    type="button"
                    onClick={startInstagramOAuth}
                    disabled={connecting}
                    className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-60"
                  >
                    {connecting ? "Connecting…" : "Add New"}
                  </button>
                </div>
              </>
            ) : (
              <button
                type="button"
                onClick={startInstagramOAuth}
                disabled={connecting}
                className="px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors disabled:opacity-60"
              >
                {connecting ? "Connecting…" : "Get Started"}
              </button>
            )}
          </div>

          {/* WhatsApp — no API doc yet, placeholder only */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-5">
            <div className="flex items-start justify-between gap-3 mb-1.5">
              <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0">
                  <FaWhatsapp size={18} className="text-white" />
                </span>
                <span className="text-base font-semibold text-gray-900 dark:text-gray-100">WhatsApp</span>
                <StatusPill connected={false} />
              </div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg px-2.5 py-1 shrink-0">
                0 Accounts
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Convert inbound enquiries into qualified leads and booked jobs.</p>
            <button
              type="button"
              onClick={() => toast("WhatsApp connect isn't wired up yet.")}
              className="px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors"
            >
              Get Started
            </button>
          </div>
        </div>

        {/* Lead Sync */}
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-8">Lead Sync</h2>
        <p className="text-[12px] font-semibold text-gray-500 dark:text-gray-100 mb-6">Sync bot leads out to a spreadsheet your team can work from</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-5">
            <div className="flex items-start justify-between gap-3 mb-1.5">
              <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0">
                  <FileSpreadsheet size={18} className="text-white" />
                </span>
                <span className="text-base font-semibold text-gray-900 dark:text-gray-100">Google Sheets</span>
                <StatusPill connected={sheetsConnected} />
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Every bot lead gets synced to a Google Sheet we create in your Drive — Google will only ask you to grant access to files this app
              creates, not your whole Drive.
            </p>

            {sheetsLoading ? (
              <div className="h-10 flex items-center text-xs text-gray-400 dark:text-gray-500">Loading…</div>
            ) : sheetsConnected ? (
              <>
                {sheetsStatus?.last_error && (
                  <div className="flex items-start gap-2.5 bg-rose-50 dark:bg-rose-500/10 border border-rose-100 dark:border-rose-500/20 rounded-xl px-4 py-3 mb-3">
                    <AlertTriangle size={15} className="text-rose-500 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-rose-700 dark:text-rose-400">Sync problem</p>
                      <p className="text-xs text-rose-600 dark:text-rose-400/90 mt-0.5 break-words">{sheetsStatus.last_error}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl px-4 py-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                      <FileSpreadsheet size={13} className="text-white" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {sheetsStatus?.sync_enabled ? "Syncing" : "Sync paused"}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">Last synced: {formatDate(sheetsStatus?.last_sync_at || undefined)}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <a
                      href={sheetsStatus?.sheet_url || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      Open Sheet <ExternalLink size={13} />
                    </a>
                    {sheetsStatus?.last_error && (
                      <button
                        type="button"
                        onClick={handleRetrySheets}
                        disabled={sheetsRetrying}
                        className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-500/15 disabled:opacity-60"
                      >
                        <RefreshCcw size={13} className={sheetsRetrying ? "animate-spin" : ""} /> {sheetsRetrying ? "Retrying…" : "Retry failed rows"}
                      </button>
                    )}
                  </div>
                  <button type="button" onClick={() => setSheetsDisconnectOpen(true)} className="text-sm font-semibold text-rose-500 hover:underline">
                    Disconnect
                  </button>
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleConnectSheets}
                  disabled={sheetsConnecting}
                  className="px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors disabled:opacity-60"
                >
                  {sheetsConnecting ? "Starting…" : awaitingSheetsAuth ? "Waiting for Google…" : "Connect Google"}
                </button>
                {awaitingSheetsAuth && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2.5">
                    Finish granting access in the tab that just opened, then come back here — we'll pick it up automatically.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <ManageAccountsModal
        isOpen={manageOpen}
        onClose={() => setManageOpen(false)}
        accounts={accounts}
        onToggle={handleToggleActive}
        onRequestDisconnect={(a) => setDisconnectTarget(a)}
        togglingId={togglingId}
      />

      <DisconnectConfirmModal
        isOpen={!!disconnectTarget}
        onClose={() => setDisconnectTarget(null)}
        onConfirm={handleDisconnect}
        submitting={disconnecting}
      />

      <DisconnectConfirmModal
        isOpen={sheetsDisconnectOpen}
        onClose={() => setSheetsDisconnectOpen(false)}
        onConfirm={handleDisconnectSheets}
        submitting={sheetsDisconnecting}
        title="Disconnect Google Sheets?"
        description="New bot leads will stop syncing to your sheet until you reconnect. The sheet itself and its existing rows stay untouched."
        confirmLabel="Disconnect"
      />
    </div>
  );
};

export default Integrations;
